import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useDeleteTaxpayerMutation,
  useGetTaxpayerByIdQuery,
  useUpdateTaxpayerMutation,
} from './taxpayersApiSlice'

const EditTaxPayerForm = ({ taxpayerId }) => {
  const navigate = useNavigate()

  const {
    data: taxpayer,
    isLoading: isLoadingTaxpayer,
    isSuccess,
    isError,
    error,
  } = useGetTaxpayerByIdQuery(taxpayerId)

  const [updateTaxpayer, { isLoading: isUpdating }] = useUpdateTaxpayerMutation()
  const [deleteTaxpayer, { isLoading: isDeleting }] = useDeleteTaxpayerMutation()

  const [formData, setFormData] = useState({
    full_name: '',
    nick_name: '',
    npwp: '',
    nik: '',
    email: '',
    phone_number: '',
    nationality: 'Indonesia',
  })

  const [errMsg, setErrMsg] = useState('')

  useEffect(() => {
    if (taxpayer) {
      setFormData({
        full_name: taxpayer.full_name || '',
        nick_name: taxpayer.nick_name || '',
        npwp: taxpayer.npwp || '',
        nik: taxpayer.nik || '',
        email: taxpayer.email || '',
        phone_number: taxpayer.phone_number || '',
        nationality: taxpayer.nationality || 'Indonesia',
      })
    }
  }, [taxpayer])

  const onChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const canSave =
    formData.full_name.trim() &&
    !isUpdating &&
    !isDeleting

  const onSaveTaxpayerClicked = async (e) => {
    e.preventDefault()
    setErrMsg('')

    if (!canSave) {
      setErrMsg('Full name is required.')
      return
    }

    try {
      await updateTaxpayer({
        id: taxpayerId,
        full_name: formData.full_name.trim(),
        nick_name: formData.nick_name.trim() || null,
        npwp: formData.npwp.trim() || null,
        nik: formData.nik.trim() || null,
        email: formData.email.trim() || null,
        phone_number: formData.phone_number.trim() || null,
        nationality: formData.nationality.trim() || 'Indonesia',
      }).unwrap()

      navigate('/taxpayers')
    } catch (err) {
      setErrMsg(err?.data?.message || err?.error || 'Failed to update taxpayer.')
    }
  }

  const onDeleteTaxpayerClicked = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this taxpayer?'
    )

    if (!confirmed) return

    setErrMsg('')

    try {
      await deleteTaxpayer({ id: taxpayerId }).unwrap()
      navigate('/taxpayers')
    } catch (err) {
      setErrMsg(err?.data?.message || err?.error || 'Failed to delete taxpayer.')
    }
  }

  if (isLoadingTaxpayer) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600 shadow-sm">
        Loading taxpayer...
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700 shadow-sm">
        {error?.data?.message || error?.error || 'Failed to load taxpayer.'}
      </div>
    )
  }

  if (!isSuccess || !taxpayer) return null

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <form onSubmit={onSaveTaxpayerClicked} className="space-y-5">
        {errMsg ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errMsg}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Full Name *
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={onChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-slate-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Nick Name
            </label>
            <input
              type="text"
              name="nick_name"
              value={formData.nick_name}
              onChange={onChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-slate-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              NPWP
            </label>
            <input
              type="text"
              name="npwp"
              value={formData.npwp}
              onChange={onChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-slate-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              NIK
            </label>
            <input
              type="text"
              name="nik"
              value={formData.nik}
              onChange={onChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-slate-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={onChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-slate-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Phone Number
            </label>
            <input
              type="text"
              name="phone_number"
              value={formData.phone_number}
              onChange={onChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-slate-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Nationality
            </label>
            <input
              type="text"
              name="nationality"
              value={formData.nationality}
              onChange={onChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-slate-500"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={!canSave}
            className="inline-flex items-center justify-center rounded-xl bg-slate-800 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isUpdating ? 'Saving...' : 'Update Taxpayer'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/taxpayers')}
            className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onDeleteTaxpayerClicked}
            disabled={isDeleting}
            className="inline-flex items-center justify-center rounded-xl border border-red-300 px-4 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default EditTaxPayerForm